import md5 from 'blueimp-md5';
import { createBlobUrlCache } from '@linc-technologies/linced-common/utils/blob-url-lru-cache';
import type MediaService from '@linc-technologies/linced-common/services/media';

// AvatarResource is a person/group-like record with an optional avatar media.
// TS-STANDIN(person, group, school): These models declare mediaId for
// AvatarImage. Replace with Pick of the three models.
export interface AvatarResource {
  id: string | null;
  mediaId?: string | null;
}

// DEFAULT_IMG allows falling back to an empty profile image when ember can't
// resolve a resource (i.e. a 404 is returned for a person).
export const DEFAULT_IMG =
  'https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y';

// Transparent 1x1 GIF shown while a shimmer plays, avoiding a broken-image
// icon before the real URL is resolved.
export const PLACEHOLDER =
  'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';

// LRU blob URL cache keyed by mediaId. Persists across component/helper
// lifecycles so tab-switching within a session doesn't re-fetch avatars that
// were already resolved. Shared by components/avatar-image.js and
// helpers/avatar-url.js so both hit the same entries.
export const avatarBlobUrlCache = createBlobUrlCache();

export function gravatarUrl(id: string): string {
  return `https://gravatar.com/avatar/${md5(id)}?d=identicon&f=y`;
}

// fallbackUrl gravatars off the resource id where we have one, otherwise the
// blank default image.
export function fallbackUrl(id: string | null | undefined): string {
  return id ? gravatarUrl(id) : DEFAULT_IMG;
}

// resolveAvatarUrl resolves the best available avatar URL for a resource,
// preferring a cached blob, then a freshly fetched blob, then gravatar, then
// the default image.
//
// Returns null when the resource hasn't loaded yet -- callers should hold their
// loading state and retry once the record resolves.
export async function resolveAvatarUrl(
  media: MediaService,
  resourceOrProxy:
    | AvatarResource
    | PromiseLike<AvatarResource | null | undefined>
    | null
    | undefined,
): Promise<string | null> {
  // Unwrap PromiseBelongsTo / PromiseObject proxy.
  const resource = await resourceOrProxy;
  if (!resource) return null;

  const { id, mediaId } = resource;
  if (!mediaId) {
    // No media record -- gravatar if we have an id, else default.
    return fallbackUrl(id);
  }

  const cached = avatarBlobUrlCache.get(mediaId);
  if (cached) return cached;

  const url = await media.getImageBlobUri(mediaId, id, 'avatar');
  if (!url) {
    // Blob fetch returned nothing, gravatar is the fallback, otherwise the
    // default.
    return fallbackUrl(id);
  }

  avatarBlobUrlCache.set(mediaId, url);
  return url;
}
